// AppointmentManagementModal.tsx
import React, { useState, useEffect } from "react";
import { Button, Space, Modal, Tooltip, Typography } from "antd";
import { EyeOutlined, CloseOutlined, ExclamationCircleOutlined, CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../../utils/auth/sessionUser";

import { Appointment, AppointmentPolicy } from "../../../types/appointments/AppointmentTypes";
import { User } from "../../../types/admin/UserTypes";

import { fetchPatientUpcomingAppointments, fetchPatientPastAppointments, cancelPatientAppointment } from "../../../api/PatientInterfaceApi";
import { DateTimeUtils } from "../../../utils/userPrefs/DateTimeUtils";
import { getCancelAvailability } from "../../../utils/healthcare/appointmentCancellation";
import { DetailDrawer, DataTable, StatusTag } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";

const { Text, Paragraph } = Typography;
const { confirm } = Modal;

interface AppointmentManagementModalProps {
	visible: boolean;
	onHide: () => void;
	onAppointmentChanged: () => void;
}

const AppointmentManagementModal: React.FC<AppointmentManagementModalProps> = ({ visible, onHide, onAppointmentChanged }) => {
	const user: User = getSessionUser();
	const { showError, showSuccess } = useNotifications();

	const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
	const [pastAppointments, setPastAppointments] = useState<Appointment[]>([]);
	const [loading, setLoading] = useState({ upcoming: true, past: true });
	const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
	const [showDetails, setShowDetails] = useState(false);
	const [activeTab, setActiveTab] = useState("upcoming");
	// The clinic's cancellation policy, served alongside the appointments. Undefined until the
	// first load resolves (and on an older backend), in which case the Cancel button behaves as
	// it always did — the backend refuses anything policy disallows either way.
	const [policy, setPolicy] = useState<AppointmentPolicy | undefined>(undefined);

	useEffect(() => {
		if (visible) loadAppointments();
	}, [visible]);

	const loadAppointments = async () => {
		setLoading({ upcoming: true, past: true });
		try {
			const [upcomingResponse, pastResponse] = await Promise.all([fetchPatientUpcomingAppointments(user.id), fetchPatientPastAppointments(user.id)]);
			setUpcomingAppointments(upcomingResponse.data);
			setPastAppointments(pastResponse.data);
			setPolicy(upcomingResponse.policy);
		} catch (error) {
			console.error("Error loading appointments:", error);
			showError("Failed to load appointments");
		} finally {
			setLoading({ upcoming: false, past: false });
		}
	};

	const formatDate = (date: Date | string | undefined) => {
		if (!date) return "N/A";
		return DateTimeUtils.formatLocalDate(date, { year: "numeric", month: "short", day: "numeric" });
	};
	const formatTime = (date: Date | string | undefined) => {
		if (!date) return "N/A";
		return DateTimeUtils.formatLocalTime(date);
	};
	const formatDateTime = (date: Date | string | undefined) => {
		if (!date) return "N/A";
		return DateTimeUtils.formatLocalDateTime(date);
	};

	const viewAppointmentDetails = (appointment: Appointment) => {
		setSelectedAppointment(appointment);
		setShowDetails(true);
	};

	const confirmCancelAppointment = (appointment: Appointment) => {
		confirm({
			title: "Cancel Appointment",
			icon: <ExclamationCircleOutlined />,
			content: `Are you sure you want to cancel your appointment on ${formatDateTime(appointment.start_time)}?`,
			okText: "Cancel Appointment",
			okType: "danger",
			onOk: () => handleCancelAppointment(appointment.id),
		});
	};

	const handleCancelAppointment = async (appointmentId: number) => {
		try {
			await cancelPatientAppointment(appointmentId);
			showSuccess("Appointment cancelled successfully");
			loadAppointments();
			onAppointmentChanged();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to cancel appointment");
		}
	};

	const upcomingColumns = [
		{
			title: "Date",
			dataIndex: "start_time",
			key: "date",
			render: (date: any) => formatDate(date),
			sorter: (a: Appointment, b: Appointment) => new Date(a.start_time || 0).getTime() - new Date(b.start_time || 0).getTime(),
		},
		{ title: "Time", dataIndex: "start_time", key: "time", render: (date: any) => formatTime(date) },
		{ title: "Title", dataIndex: "title", key: "title", ellipsis: true },
		{ title: "Location", dataIndex: "location", key: "location" },
		{
			title: "Actions",
			key: "actions",
			render: (_: any, record: Appointment) => {
				// Past / already-cancelled / cancellation-switched-off → no button at all.
				// Inside the notice window → shown but DISABLED, with the reason: the patient has a
				// real appointment and needs to know why they cannot cancel it here.
				const cancelState = getCancelAvailability(record, policy);
				return (
					<Space size="small">
						<Button type="text" size="small" icon={<EyeOutlined />} onClick={() => viewAppointmentDetails(record)} />
						{cancelState.kind === "enabled" && (
							<Button
								type="text"
								size="small"
								danger
								icon={<CloseOutlined />}
								onClick={() => confirmCancelAppointment(record)}
								data-testid={`patient-portal-cancel-appointment-${record.id}`}
							/>
						)}
						{cancelState.kind === "disabled" && (
							<Tooltip title={cancelState.reason}>
								{/* A disabled Ant button swallows pointer events, so the Tooltip needs a wrapper to hover on. */}
								<span>
									<Button type="text" size="small" danger disabled icon={<CloseOutlined />} data-testid={`patient-portal-cancel-appointment-${record.id}`} />
								</span>
							</Tooltip>
						)}
					</Space>
				);
			},
		},
	];

	const pastColumns = [
		{
			title: "Date",
			dataIndex: "start_time",
			key: "date",
			render: (date: any) => formatDate(date),
			sorter: (a: Appointment, b: Appointment) => new Date(a.start_time || 0).getTime() - new Date(b.start_time || 0).getTime(),
		},
		{ title: "Time", dataIndex: "start_time", key: "time", render: (date: any) => formatTime(date) },
		{ title: "Title", dataIndex: "title", key: "title", ellipsis: true },
		{ title: "Type", dataIndex: "location", key: "location" },
		{
			title: "Actions",
			key: "actions",
			render: (_: any, record: Appointment) => <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => viewAppointmentDetails(record)} />,
		},
	];

	const tabItems = [
		{
			key: "upcoming",
			label: (
				<span>
					<CalendarOutlined style={{ marginRight: 6 }} />
					Upcoming
				</span>
			),
			children: <DataTable dataSource={upcomingAppointments} columns={upcomingColumns} rowKey="id" loading={loading.upcoming} bare />,
		},
		{
			key: "past",
			label: (
				<span>
					<ClockCircleOutlined style={{ marginRight: 6 }} />
					Past
				</span>
			),
			children: <DataTable dataSource={pastAppointments} columns={pastColumns} rowKey="id" loading={loading.past} bare />,
		},
	];

	return (
		<>
			<DetailDrawer open={visible} onClose={onHide} title="My Appointments" width="70vw" tabs={tabItems} activeTab={activeTab} onTabChange={setActiveTab} />

			<DetailDrawer open={showDetails} onClose={() => setShowDetails(false)} title="Appointment Details" width="45vw">
				{selectedAppointment && (
					<Space direction="vertical" size="middle" style={{ width: "100%" }}>
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Date
							</Text>
							<Text style={{ fontSize: 13 }}>{formatDate(selectedAppointment.start_time)}</Text>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Time
							</Text>
							<Text style={{ fontSize: 13 }}>
								{formatTime(selectedAppointment.start_time)} - {formatTime(selectedAppointment.end_time)}
							</Text>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Type
							</Text>
							<Text style={{ fontSize: 13 }}>{selectedAppointment.location || "N/A"}</Text>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Category
							</Text>
							<Text style={{ fontSize: 13 }}>{selectedAppointment.category || "N/A"}</Text>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Title
							</Text>
							<Text style={{ fontSize: 13 }}>{selectedAppointment.title || "N/A"}</Text>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								Status
							</Text>
							<StatusTag status={selectedAppointment.status || "pending"} />
						</div>
						{(selectedAppointment as any).notes && (
							<div>
								<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
									Notes
								</Text>
								<Paragraph style={{ fontSize: 13 }}>{(selectedAppointment as any).notes}</Paragraph>
							</div>
						)}
						{selectedAppointment.consultation_link && (
							<div>
								<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
									Video Consultation
								</Text>
								<a href={selectedAppointment.consultation_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
									Join Video Call
								</a>
							</div>
						)}
						<div style={{ marginTop: 16, padding: 12, backgroundColor: "var(--mg-bg-subtle)", borderRadius: 4 }}>
							<Text type="secondary" style={{ fontSize: 11 }}>
								<strong>Timezone:</strong> {DateTimeUtils.getUserTimezone()} ({DateTimeUtils.getTimezoneOffset()})
							</Text>
						</div>
					</Space>
				)}
			</DetailDrawer>
		</>
	);
};

export default AppointmentManagementModal;
